import React, { useEffect, useState } from 'react';

import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import GroupSegregationSection from './GroupSegregationSection';

const positionMap = { left: 'flex-start', center: 'center', right: 'flex-end' };

const ACTION_LABELS = {
  external: 'Abre em nova aba',
  modal: 'Abre em modal',
};

export default function LinkButton({
  deleteComponent,
  rowId,
  colId,
  attributes,
  setAttributes,
  autoOpen = false,
  groups = [],
}) {
  // State do formulário
  const [btnText, setBtnText] = useState(attributes?.btnText ?? 'Clique aqui');
  const [fontColor, setFontColor] = useState(attributes?.fontColor ?? '#fff');
  const [btnColor, setBtnColor] = useState(attributes?.btnColor ?? '#007bff');
  const [btnPosition, setBtnPosition] = useState(attributes?.btnPosition ?? 'center');
  const [linkUrl, setLinkUrl] = useState(attributes?.linkUrl ?? '');
  const [linkAction, setLinkAction] = useState(attributes?.linkAction ?? 'external');
  // Segregação por grupos
  const [groupIds, setGroupIds] = useState(attributes?.groupIds || []);

  // Edit Modal
  const [editModal, setEditModal] = useState(false);
  const [urlError, setUrlError] = useState(false);

  const fontTipId = `link-font-tip-${rowId}-${colId}`;
  const colorTipId = `link-color-tip-${rowId}-${colId}`;

  useEffect(() => {
    if (autoOpen) setEditModal(true);
  }, []);

  useEffect(() => {
    if (!attributes) {
      setAttributes({
        rowId,
        colId,
        attributes: {
          inputName: 'Botao de link',
          btnText: 'Clique aqui',
          fontColor: '#fff',
          btnColor: '#007bff',
          btnPosition: 'center',
          linkUrl: '',
          linkAction: 'external',
        },
      });
    }
  }, [rowId, colId, setAttributes, attributes]);

  const submitForm = (e) => {
    e.preventDefault();
    if (!linkUrl.trim()) {
      setUrlError(true);
      return;
    }
    setUrlError(false);
    setAttributes({
      rowId,
      colId,
      attributes: {
        inputName: 'Botao de link',
        btnText,
        fontColor,
        btnColor,
        btnPosition,
        linkUrl,
        linkAction,
        ...(groupIds.length > 0 ? { groupIds } : {}),
      },
    });
    setEditModal(false);
  };

  return (
    <div className="custom-input-container">
      <Modal size="lg" isOpen={editModal} toggle={() => setEditModal(!editModal)} centered>
        <ModalHeader toggle={() => setEditModal(false)}>
          Configurar Botão com Link
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col>
              <Form>
                <FormGroup className="input-margin-btm">
                  <Label htmlFor={`link-btn-text-${rowId}-${colId}`} className="form-label">
                    Texto do Botão
                  </Label>
                  <Input
                    id={`link-btn-text-${rowId}-${colId}`}
                    type="text"
                    value={btnText}
                    onChange={({ target }) => setBtnText(target.value)}
                  />
                </FormGroup>
                <FormGroup className="input-margin-btm">
                  <Label htmlFor={`link-btn-url-${rowId}-${colId}`} className="form-label">
                    URL do Link *
                  </Label>
                  <Input
                    id={`link-btn-url-${rowId}-${colId}`}
                    type="text"
                    placeholder="https://..."
                    value={linkUrl}
                    onChange={({ target }) => {
                      setLinkUrl(target.value);
                      setUrlError(false);
                    }}
                  />
                  {urlError && (
                    <span style={{ color: 'red', fontSize: '12px' }}>
                      URL é obrigatória
                    </span>
                  )}
                </FormGroup>
                <FormGroup className="input-margin-btm">
                  <Label htmlFor={`link-btn-action-${rowId}-${colId}`} className="form-label">
                    Ação ao clicar
                  </Label>
                  <Input
                    id={`link-btn-action-${rowId}-${colId}`}
                    type="select"
                    value={linkAction}
                    onChange={({ target }) => setLinkAction(target.value)}
                  >
                    <option value="external">Abrir fora do formulário (nova aba)</option>
                    <option value="modal">Abrir em modal</option>
                  </Input>
                </FormGroup>
                <FormGroup className="input-margin-btm">
                  <div style={{ display: 'flex' }}>
                    <Label htmlFor={`link-font-color-${rowId}-${colId}`} className="form-label">
                      Cor do texto
                    </Label>
                    <div>
                      <div className="show-tips" id={fontTipId}>
                        <span>?</span>
                      </div>
                      <UncontrolledTooltip
                        style={{ backgroundColor: '#6f42c1' }}
                        placement="right"
                        target={fontTipId}
                      >
                        RGB, HEX ou nome da cor em inglês
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <Input
                    id={`link-font-color-${rowId}-${colId}`}
                    type="text"
                    value={fontColor}
                    onChange={({ target }) => setFontColor(target.value)}
                  />
                </FormGroup>
                <FormGroup className="input-margin-btm">
                  <div style={{ display: 'flex' }}>
                    <Label htmlFor={`link-btn-color-${rowId}-${colId}`} className="form-label">
                      Cor do botão
                    </Label>
                    <div>
                      <div className="show-tips" id={colorTipId}>
                        <span>?</span>
                      </div>
                      <UncontrolledTooltip
                        style={{ backgroundColor: '#6f42c1' }}
                        placement="right"
                        target={colorTipId}
                      >
                        RGB, HEX ou nome da cor em inglês
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <Input
                    id={`link-btn-color-${rowId}-${colId}`}
                    type="text"
                    value={btnColor}
                    onChange={({ target }) => setBtnColor(target.value)}
                  />
                </FormGroup>
                <FormGroup className="input-margin-btm">
                  <Label htmlFor={`link-btn-position-${rowId}-${colId}`} className="form-label">
                    Posição do botão
                  </Label>
                  <Input
                    id={`link-btn-position-${rowId}-${colId}`}
                    type="select"
                    value={btnPosition}
                    onChange={({ target }) => setBtnPosition(target.value)}
                  >
                    <option value="left">Esquerda</option>
                    <option value="center">Centro</option>
                    <option value="right">Direita</option>
                  </Input>
                </FormGroup>
              </Form>
              <GroupSegregationSection
                groups={groups}
                groupIds={groupIds}
                onChange={setGroupIds}
                fieldPrefix={`linkbutton-${rowId}-${colId}`}
              />
            </Col>
            <Col style={{ borderLeft: '1px solid gray' }}>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: positionMap[btnPosition] || 'center',
                }}
              >
                <Button style={{ backgroundColor: btnColor }}>
                  <span style={{ color: fontColor }}>{btnText}</span>
                </Button>
              </div>
            </Col>
          </Row>
          <div className="save-btn-form-container">
            <Button
              color="success"
              className="btn btn-success waves-effect waves-light save-btn-form"
              onClick={submitForm}
            >
              Salvar
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <div className="displayed-config-container">
        <div style={{ width: '100%' }}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: positionMap[btnPosition] || 'center',
            }}
          >
            <Button className="displayed-label" style={{ backgroundColor: btnColor }}>
              <span style={{ color: fontColor }}>{btnText}</span>
            </Button>
          </div>
        </div>
        <div className="config-form-icon">
          <span
            className="mdi mdi-cog-outline"
            onClick={() => setEditModal(!editModal)}
          />
          <span
            className="mdi mdi-close close-icon"
            title="Remover Campo"
            onClick={() => deleteComponent(rowId, colId)}
          />
        </div>
      </div>
    </div>
  );
}
